import { useEffect, useState } from 'react';
import { Client } from '@stomp/stompjs';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/auth/accessTokenStore';
import { refreshSession } from '@/auth/session';

export function usePipelineLive(tenantId: string | undefined, identity: string | null) {
  const queries = useQueryClient();
  const [connectedTenant, setConnectedTenant] = useState<string | null>(null);
  useEffect(() => {
    if (!tenantId) return;
    let stopped = false;
    const resync = () => {
      if (!stopped) {
        void queries.invalidateQueries({ queryKey: ['pipeline', identity] });
        // A deal moving is what puts work on Today and takes it off.
        void queries.invalidateQueries({ queryKey: ['work'] });
      }
    };
    const url = new URL('/api/dispatch/live', window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const client = new Client({
      brokerURL: url.href,
      reconnectDelay: 1000,
      connectionTimeout: 8000,
      heartbeatIncoming: 0,
      heartbeatOutgoing: 10000,
      beforeConnect: async () => {
        if (!getAccessToken()) await refreshSession();
        client.connectHeaders = { Authorization: 'Bearer ' + (getAccessToken() ?? '') };
      },
      onConnect: () => {
        client.subscribe('/topic/pipeline/' + tenantId, resync);
        setConnectedTenant(tenantId);
        resync();
      },
      onWebSocketClose: () => {
        if (!stopped) setConnectedTenant(null);
      },
      onStompError: () => {
        void refreshSession();
      },
    });
    client.activate();
    // Periodic resync also covers a connection that stays open across a missed notification.
    const timer = window.setInterval(resync, 30000);
    return () => {
      stopped = true;
      clearInterval(timer);
      void client.deactivate();
    };
  }, [tenantId, identity, queries]);
  return tenantId !== undefined && connectedTenant === tenantId;
}
